import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import {
  API_ROUTES,
  KEBAB_MAX_AVATAR_BYTES,
  KEBAB_MAX_PHOTO_BYTES,
  KEBAB_PREVIEW_LIMIT,
  kebabChartColor,
  kebabCountdown,
  kebabLocalIsoDay,
  formatKebabPrice,
  pluralKebabs,
  type KebabBoard,
  type KebabContestant,
  type KebabEntry,
} from "@ziolo/shared";
import { Button, Card } from "@ziolo/ui";
import { apiRequest, apiUpload, errorMessage } from "../lib/apiClient.js";
import { formatTimestamp } from "../lib/format.js";
import { useFormSubmit } from "../hooks/useFormSubmit.js";
import { useSession } from "../session/SessionProvider.js";
import { Field } from "../components/Field.js";
import { AdminPanel } from "../components/admin/AdminChrome.js";
import { KebabAdminTools } from "../components/admin/KebabAdminTools.js";
import { KebabCharts } from "../games/kebab/KebabCharts.js";
import { KebabLightbox } from "../games/kebab/KebabLightbox.js";
import { confirmAdmin, useIsAdmin } from "../hooks/useIsAdmin.js";
import "../styles/kebab.css";

const COUNTDOWN_LABEL = {
  before: "Czas do rozpoczęcia wyzwania",
  during: "Zostało czasu",
  after: "Wyzwanie zakończone",
} as const;

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

export function KebabPage() {
  const { user } = useSession();
  const isAdmin = useIsAdmin();
  const { busy, submit } = useFormSubmit();
  const [board, setBoard] = useState<KebabBoard | null>(null);
  const [entries, setEntries] = useState<KebabEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [lightboxId, setLightboxId] = useState<number | null>(null);
  const [lightboxFilter, setLightboxFilter] = useState<number | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const reload = useCallback(async () => {
    const [nextBoard, nextEntries] = await Promise.all([
      apiRequest<KebabBoard>(API_ROUTES.kebab.board),
      apiRequest<KebabEntry[]>(API_ROUTES.kebab.entries),
    ]);
    setBoard(nextBoard);
    setEntries(nextEntries);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      apiRequest<KebabBoard>(API_ROUTES.kebab.board, { signal: controller.signal }),
      apiRequest<KebabEntry[]>(API_ROUTES.kebab.entries, { signal: controller.signal }),
    ])
      .then(([nextBoard, nextEntries]) => {
        setBoard(nextBoard);
        setEntries(nextEntries);
        setError(null);
      })
      .catch((cause) => {
        if (!controller.signal.aborted) setError(errorMessage(cause));
      });
    return () => controller.abort();
  }, [user?.id]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const countdown = board ? kebabCountdown(now, board.startDate, board.endDate) : null;
  const canAdd = Boolean(user && board?.meContestantId);
  const canViewPhotos = Boolean(board?.canViewPhotos);
  const lightboxEntries = useMemo(() => {
    if (lightboxFilter === null) return entries;
    return entries.filter((entry) => entry.contestantId === lightboxFilter);
  }, [entries, lightboxFilter]);

  function openLightbox(entryId: number, contestantId?: number) {
    if (!canViewPhotos) return;
    setLightboxFilter(contestantId ?? null);
    setLightboxId(entryId);
  }

  function stepLightbox(delta: number) {
    if (lightboxId === null) return;
    const index = lightboxEntries.findIndex((entry) => entry.id === lightboxId);
    if (index < 0 || lightboxEntries.length === 0) return;
    const next = (index + delta + lightboxEntries.length) % lightboxEntries.length;
    setLightboxId(lightboxEntries[next]?.id ?? null);
  }

  return (
    <div className="kb-page">
      <header className="kb-head">
        <h1>🥙 Kebab Tracker</h1>
        <p>Wyzwanie: 10 lutego 2026 — 10 lutego 2027.</p>
      </header>

      <AdminPanel title="Kebab">
        <KebabAdminTools onChanged={() => void reload()} />
      </AdminPanel>

      {error ? <p className="kb-error">{error}</p> : null}

      {countdown && board ? (
        <Card className="kb-countdown">
          <span className="kb-countdown__label">{COUNTDOWN_LABEL[countdown.phase]}</span>
          <div className="kb-countdown__value" aria-live="polite">
            <span className="kb-countdown__num">{pad(countdown.days)}</span>
            <span className="kb-countdown__unit">dni</span>
            <span className="kb-countdown__num">{pad(countdown.hours)}</span>
            <span className="kb-countdown__unit">godz.</span>
            <span className="kb-countdown__num">{pad(countdown.minutes)}</span>
            <span className="kb-countdown__unit">min</span>
            <span className="kb-countdown__num">{pad(countdown.seconds)}</span>
            <span className="kb-countdown__unit">s</span>
          </div>
        </Card>
      ) : null}

      {board ? (
        <KebabCharts
          contestants={board.contestants}
          entries={entries}
          startDate={board.startDate}
          endDate={board.endDate}
          now={now}
        />
      ) : null}

      {!canViewPhotos ? (
        <p className="kb-hint">
          {user
            ? "Zdjęcia widzą tylko uczestnicy wyzwania."
            : "Zaloguj się jako uczestnik, żeby zobaczyć zdjęcia."}
        </p>
      ) : null}

      <div className="kb-grid">
        {board?.contestants.map((contestant, index) => (
          <ContestantCard
            key={contestant.id}
            contestant={contestant}
            color={kebabChartColor(index)}
            canViewPhotos={canViewPhotos}
            onOpen={(entryId) => openLightbox(entryId)}
            onViewAll={() => {
              const first = entries.find((entry) => entry.contestantId === contestant.id);
              if (first) openLightbox(first.id, contestant.id);
            }}
            onAvatar={() => {
              if (contestant.isMe) setAvatarOpen(true);
            }}
          />
        ))}
      </div>

      {!user ? (
        <p className="kb-hint">Zaloguj się kontem uczestnika, żeby dodać kebaba.</p>
      ) : !canAdd ? (
        <p className="kb-hint">
          To konto nie jest na liście uczestników. Uczestnicy logują się tą samą nazwą co na
          trackerze (np. Norbert).
        </p>
      ) : null}

      {canAdd ? (
        <button
          type="button"
          className="kb-fab"
          onClick={() => setAddOpen(true)}
          aria-label="Dodaj kebab"
        >
          +
        </button>
      ) : null}

      {addOpen ? (
        <AddKebabModal
          busy={busy}
          onClose={() => setAddOpen(false)}
          onSubmit={async (form) => {
            const ok = await submit(async () => {
              await apiUpload(API_ROUTES.kebab.entries, form);
              await reload();
            }, "Dodano kebaba");
            if (ok) setAddOpen(false);
          }}
        />
      ) : null}

      {avatarOpen ? (
        <AvatarModal
          busy={busy}
          onClose={() => setAvatarOpen(false)}
          onSubmit={async (form) => {
            const ok = await submit(async () => {
              await apiUpload(API_ROUTES.kebab.avatar, form);
              await reload();
            }, "Zaktualizowano avatar");
            if (ok) setAvatarOpen(false);
          }}
        />
      ) : null}

      <KebabLightbox
        entries={lightboxEntries}
        currentId={lightboxId}
        onClose={() => {
          setLightboxId(null);
          setLightboxFilter(null);
        }}
        onStep={stepLightbox}
        onDelete={
          isAdmin
            ? async (id) => {
                if (!confirmAdmin("Usunąć ten kebab i zdjęcie?")) return;
                const ok = await submit(async () => {
                  await apiRequest(API_ROUTES.admin.kebabEntry(id), { method: "DELETE" });
                  await reload();
                }, "Usunięto wpis");
                if (ok) {
                  setLightboxId(null);
                  setLightboxFilter(null);
                }
              }
            : undefined
        }
      />
    </div>
  );
}

function ContestantCard({
  contestant,
  color,
  canViewPhotos,
  onOpen,
  onViewAll,
  onAvatar,
}: {
  contestant: KebabContestant;
  color: string;
  canViewPhotos: boolean;
  onOpen: (entryId: number) => void;
  onViewAll: () => void;
  onAvatar: () => void;
}) {
  const initial = contestant.name.charAt(0);
  return (
    <Card className="kb-card">
      <div className="kb-card__head">
        <button
          type="button"
          className="kb-avatar"
          onClick={onAvatar}
          disabled={!contestant.isMe}
          aria-label={contestant.isMe ? "Zmień avatar" : contestant.name}
        >
          {canViewPhotos && contestant.avatarUrl ? (
            <img src={contestant.avatarUrl} alt="" />
          ) : (
            <span style={{ color }}>{initial}</span>
          )}
        </button>
        <div>
          <div className="kb-card__name">
            {contestant.name}
            {contestant.isMe ? <span className="kb-card__you">ty</span> : null}
          </div>
          <div className="kb-card__count" style={{ color }}>
            {pluralKebabs(contestant.count)}
          </div>
          {contestant.totalPrice > 0 ? (
            <div className="kb-card__price">{formatKebabPrice(contestant.totalPrice)}</div>
          ) : null}
        </div>
      </div>
      <div className="kb-thumbs">
        {contestant.preview.length === 0 ? (
          <p className="kb-empty">Brak kebabów</p>
        ) : (
          contestant.preview.map((entry) =>
            canViewPhotos && entry.imageUrl ? (
              <button
                key={entry.id}
                type="button"
                className="kb-thumb"
                onClick={() => onOpen(entry.id)}
              >
                <img src={entry.imageUrl} alt="" />
                <span className="kb-thumb__meta">
                  {formatTimestamp(entry.createdAt)}
                  {entry.location ? ` · ${entry.location}` : ""}
                </span>
              </button>
            ) : (
              <div key={entry.id} className="kb-thumb kb-thumb--locked">
                <span className="kb-thumb__lock" aria-hidden="true">
                  🔒
                </span>
                <span className="kb-thumb__meta">
                  {formatTimestamp(entry.createdAt)}
                  {entry.location ? ` · ${entry.location}` : ""}
                </span>
              </div>
            ),
          )
        )}
      </div>
      {canViewPhotos && contestant.preview.length >= KEBAB_PREVIEW_LIMIT ? (
        <Button variant="ghost" block onClick={onViewAll}>
          Zobacz wszystkie
        </Button>
      ) : null}
    </Card>
  );
}

function AddKebabModal({
  busy,
  onClose,
  onSubmit,
}: {
  busy: boolean;
  onClose: () => void;
  onSubmit: (form: FormData) => Promise<void>;
}) {
  const [preview, setPreview] = useState<string | null>(null);
  const today = kebabLocalIsoDay(new Date());

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    await onSubmit(form);
  }

  return (
    <div
      className="kb-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kb-add-title"
      onClick={onClose}
    >
      <form className="kb-modal__card" onSubmit={(event) => void handleSubmit(event)} onClick={(event) => event.stopPropagation()}>
        <div className="kb-modal__head">
          <h2 id="kb-add-title">Dodaj nowy kebab</h2>
          <button type="button" className="kb-modal__close" onClick={onClose} aria-label="Zamknij">
            ×
          </button>
        </div>
        <Field label="Data" hint="Kiedy zjadłeś — domyślnie dziś.">
          <input
            className="z-input"
            type="date"
            name="eatenAt"
            defaultValue={today}
            max={today}
            required
          />
        </Field>
        <Field label="Lokalizacja (opcjonalnie)">
          <input className="z-input" type="text" name="location" placeholder="Gdzie zjadłeś tego kebaba?" />
        </Field>
        <Field label="Cena (opcjonalnie)">
          <input className="z-input" type="number" name="price" step="0.01" min="0" placeholder="0,00" />
        </Field>
        <Field label="Notatki (opcjonalnie)">
          <textarea className="z-textarea" name="notes" rows={3} placeholder="Jak było?" />
        </Field>
        <Field label="Zdjęcie" hint={`JPEG, PNG, WebP lub HEIC. Max ${KEBAB_MAX_PHOTO_BYTES / (1024 * 1024)} MB.`}>
          <input
            className="z-input"
            type="file"
            name="image"
            accept="image/*,image/heic,image/heif"
            capture="environment"
            required
            onChange={(event) => {
              const file = event.target.files?.[0];
              setPreview(file ? URL.createObjectURL(file) : null);
            }}
          />
        </Field>
        {preview ? <img className="kb-preview" src={preview} alt="Podgląd" /> : null}
        <div className="kb-modal__actions">
          <Button type="button" onClick={onClose}>
            Anuluj
          </Button>
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? "Wysyłanie..." : "Dodaj kebab"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function AvatarModal({
  busy,
  onClose,
  onSubmit,
}: {
  busy: boolean;
  onClose: () => void;
  onSubmit: (form: FormData) => Promise<void>;
}) {
  const [preview, setPreview] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    await onSubmit(form);
  }

  return (
    <div
      className="kb-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kb-avatar-title"
      onClick={onClose}
    >
      <form className="kb-modal__card" onSubmit={(event) => void handleSubmit(event)} onClick={(event) => event.stopPropagation()}>
        <div className="kb-modal__head">
          <h2 id="kb-avatar-title">Zmień avatar</h2>
          <button type="button" className="kb-modal__close" onClick={onClose} aria-label="Zamknij">
            ×
          </button>
        </div>
        <Field label="Zdjęcie" hint={`JPEG, PNG lub WebP. Max ${KEBAB_MAX_AVATAR_BYTES / (1024 * 1024)} MB.`}>
          <input
            className="z-input"
            type="file"
            name="avatar"
            accept="image/jpeg,image/png,image/webp"
            capture="user"
            required
            onChange={(event) => {
              const file = event.target.files?.[0];
              setPreview(file ? URL.createObjectURL(file) : null);
            }}
          />
        </Field>
        {preview ? <img className="kb-preview kb-preview--round" src={preview} alt="Podgląd" /> : null}
        <div className="kb-modal__actions">
          <Button type="button" onClick={onClose}>
            Anuluj
          </Button>
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? "Wysyłanie..." : "Zaktualizuj"}
          </Button>
        </div>
      </form>
    </div>
  );
}
